// src/views/WelcomeView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { playSound } from '../utils/sounds';
import { getMascotDialogue } from '../data/mascotDialogues';
import Mascot from '../components/Mascot';
import BrandLogo from '../components/BrandLogo';
import { Play, BookOpen, Globe, Shield } from 'lucide-react';
import { SKINS_DATA } from '../data/skins';

export default function WelcomeView({ setView, userProgress, onEquipSkin }) {
  const currentSkin = userProgress?.currentSkin || 'bribark';
  const currentSkinData = SKINS_DATA.find(s => s.id === currentSkin);
  const isLegendary = currentSkinData?.rarity === 'legendary';

  const currentFamily = currentSkin.startsWith('dolly') ? 'dollybelle' : 'bribark';
  const bribarkSkin = currentSkin.startsWith('bribark') ? currentSkin : 'bribark';
  const dollySkin = currentSkin.startsWith('dolly') ? currentSkin : 'dollybelle';

  const [selectedChar, setSelectedChar] = useState(currentFamily);
  const [bribarkMsg, setBribarkMsg] = useState(null);
  const [dollyMsg, setDollyMsg] = useState(null);
  const [idleActive, setIdleActive] = useState(true);
  const [jumping, setJumping] = useState({ bribark: false, dollybelle: false });
  const [syncStep, setSyncStep] = useState(0); // Para animación sync bar

  const jumpTimeoutRef = useRef({ bribark: null, dollybelle: null });
  const idleIntervalRef = useRef(null);

  // Animación de la barra sync
  useEffect(() => {
    const interval = setInterval(() => {
      setSyncStep(prev => (prev + 1) % 3);
    }, 900);
    return () => clearInterval(interval);
  }, []);

  const triggerJump = (charBase) => {
    if (jumpTimeoutRef.current[charBase]) clearTimeout(jumpTimeoutRef.current[charBase]);
    setJumping(prev => ({ ...prev, [charBase]: true }));
    jumpTimeoutRef.current[charBase] = setTimeout(() => {
      setJumping(prev => ({ ...prev, [charBase]: false }));
      jumpTimeoutRef.current[charBase] = null;
    }, 600);
  };

  // ✅ EFECTO 1: Saludo inicial (sin cambios)
  useEffect(() => {
    const msg = getMascotDialogue('welcome_greeting', currentSkin, isLegendary);
    if (currentFamily === 'bribark') {
      setBribarkMsg(msg);
      setTimeout(() => triggerJump('bribark'), 100);
    } else {
      setDollyMsg(msg);
      setTimeout(() => triggerJump('dollybelle'), 100);
    }
    const clearGreeting = setTimeout(() => {
      setBribarkMsg(null);
      setDollyMsg(null);
    }, 3000);
    return () => clearTimeout(clearGreeting);
  }, []);

  // ✅ EFECTO 2: Idle messages (sin cambios)
  useEffect(() => {
    if (!idleActive) return;
    const showIdleMessages = () => {
      if (selectedChar === 'bribark') {
        setBribarkMsg(getMascotDialogue('welcome_ready', bribarkSkin, isLegendary));
        setDollyMsg(getMascotDialogue('welcome_select_me', dollySkin, isLegendary));
        if (Math.random() > 0.5) triggerJump('dollybelle');
      } else {
        setDollyMsg(getMascotDialogue('welcome_ready', dollySkin, isLegendary));
        setBribarkMsg(getMascotDialogue('welcome_select_me', bribarkSkin, isLegendary));
        if (Math.random() > 0.5) triggerJump('bribark');
      }
    };
    showIdleMessages();
    idleIntervalRef.current = setInterval(showIdleMessages, 4000);
    return () => {
      if (idleIntervalRef.current) {
        clearInterval(idleIntervalRef.current);
        idleIntervalRef.current = null;
      }
    };
  }, [idleActive, selectedChar, bribarkSkin, dollySkin, isLegendary]);

  // ✅ MANEJADOR: Selección (sin cambios)
  const handleCharSelect = (charBase) => {
    if (selectedChar === charBase) return;
    playSound('selection');
    setSelectedChar(charBase);
    triggerJump(charBase);
    const skinToEquip = charBase === 'bribark' ? bribarkSkin : dollySkin;
    onEquipSkin(skinToEquip);
    const selectedMsg = getMascotDialogue('welcome_selection', skinToEquip, isLegendary);
    const unselectedSkin = charBase === 'bribark' ? dollySkin : bribarkSkin;
    const unselectedMsg = getMascotDialogue('welcome_select_me', unselectedSkin, isLegendary);
    if (charBase === 'bribark') {
      setBribarkMsg(selectedMsg);
      setDollyMsg(unselectedMsg);
    } else {
      setDollyMsg(selectedMsg);
      setBribarkMsg(unselectedMsg);
    }
  };

  const handleStartClick = () => {
    playSound('start');
    setIdleActive(false);
    setTimeout(() => setView('lobby'), 300);
  };

  const syncIcons = [
    <BookOpen size={16} className="text-blue-300" />,
    <Globe size={16} className="text-sky-300" />,
    <Shield size={16} className="text-blue-200" />,
  ];

  return (
    <div className="relative min-h-[100dvh] flex flex-col overflow-hidden">

      {/* ── CAPA 0: Imagen de fondo ── */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/images/camino.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      />

      {/* ── CAPA 1: Overlay top — para legibilidad del header ── */}
      <div className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, rgba(10,20,60,0.82) 0%, rgba(10,20,60,0.5) 30%, transparent 55%, rgba(10,20,60,0.55) 75%, rgba(10,20,60,0.90) 100%)'
        }}
      />

      {/* ── CAPA 2: Partículas decorativas ── */}
      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
        {[
          { top: '8%',  left: '10%', size: 'w-2 h-2',   delay: '0s',    color: 'bg-sky-300' },
          { top: '15%', left: '82%', size: 'w-3 h-3',   delay: '0.7s',  color: 'bg-blue-300' },
          { top: '22%', left: '55%', size: 'w-1.5 h-1.5',delay: '1.2s', color: 'bg-white' },
          { top: '12%', left: '40%', size: 'w-2 h-2',   delay: '0.4s',  color: 'bg-cyan-300' },
          { top: '5%',  left: '68%', size: 'w-1.5 h-1.5',delay:'1.8s',  color: 'bg-sky-200' },
          { top: '30%', left: '8%',  size: 'w-1.5 h-1.5',delay:'0.9s',  color: 'bg-white' },
        ].map((p, i) => (
          <div key={i}
            className={`absolute ${p.size} ${p.color} rounded-full opacity-70 animate-bounce`}
            style={{ top: p.top, left: p.left, animationDelay: p.delay, animationDuration: '2.5s' }}
          />
        ))}
      </div>

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div className="relative z-20 flex flex-col justify-between min-h-[100dvh] px-4 pt-6 pb-8 animate-in fade-in duration-1000">

       {/* HEADER */}
<div className="text-center space-y-2 animate-in slide-in-from-top-8 duration-700">
  <div className="flex justify-center">
    <div className="bg-white/85 backdrop-blur-sm px-5 py-2.5 rounded-2xl shadow-xl border border-white/60">
      <BrandLogo />
    </div>
  </div>

  <h1
    className="text-5xl font-black tracking-tighter mt-3 uppercase"
    style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #bae6fd 50%, #7dd3fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      filter: 'drop-shadow(0 0 20px rgba(125,211,252,0.8))',
    }}
  >
    ¡Bienvenido!
  </h1>

  <p className="text-sky-200 font-semibold italic text-sm drop-shadow-md tracking-wide">
    Elige tu compañero de aventura
  </p>
</div>


        {/* MASCOTAS */}
        <div className="flex justify-center items-end gap-4 py-4">

          {/* BRIBARK */}
          <div
            className="flex flex-col items-center cursor-pointer transition-all duration-500"
            style={{ transform: selectedChar === 'bribark' ? 'scale(1.12)' : 'scale(0.88)' }}
            onClick={() => handleCharSelect('bribark')}
          >
            <div
              className="rounded-full p-1 transition-all duration-500"
              style={selectedChar === 'bribark' ? {
                boxShadow: '0 0 0 4px rgba(34,211,238,0.5), 0 0 30px rgba(34,211,238,0.6), 0 0 60px rgba(34,211,238,0.3)',
                background: 'radial-gradient(circle, rgba(34,211,238,0.15) 0%, transparent 70%)',
                borderRadius: '9999px',
              } : {
                filter: 'grayscale(0.8)',
                opacity: 0.65,
              }}
            >
              <Mascot type={bribarkSkin} message={bribarkMsg} isJumping={jumping.bribark} />
            </div>
            <span
              className={`mt-2 text-xs font-black tracking-widest px-3 py-1 rounded-full border transition-all duration-500
                ${selectedChar === 'bribark'
                  ? 'bg-cyan-400/20 text-cyan-200 border-cyan-400/40 shadow-lg shadow-cyan-500/30'
                  : 'bg-white/5 text-white/30 border-white/10'
                }`}
            >
              BRIBARK
            </span>
          </div>

          {/* DOLLYBELLE */}
          <div
            className="flex flex-col items-center cursor-pointer transition-all duration-500"
            style={{ transform: selectedChar === 'dollybelle' ? 'scale(1.12)' : 'scale(0.88)' }}
            onClick={() => handleCharSelect('dollybelle')}
          >
            <div
              className="rounded-full p-1 transition-all duration-500"
              style={selectedChar === 'dollybelle' ? {
                boxShadow: '0 0 0 4px rgba(244,114,182,0.5), 0 0 30px rgba(244,114,182,0.6), 0 0 60px rgba(244,114,182,0.3)',
                background: 'radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 70%)',
                borderRadius: '9999px',
              } : {
                filter: 'grayscale(0.8)',
                opacity: 0.65,
              }}
            >
              <Mascot type={dollySkin} message={dollyMsg} isJumping={jumping.dollybelle} />
            </div>
            <span
              className={`mt-2 text-xs font-black tracking-widest px-3 py-1 rounded-full border transition-all duration-500
                ${selectedChar === 'dollybelle'
                  ? 'bg-pink-400/20 text-pink-200 border-pink-400/40 shadow-lg shadow-pink-500/30'
                  : 'bg-white/5 text-white/30 border-white/10'
                }`}
            >
              DOLLYBELLE
            </span>
          </div>
        </div>

        {/* BOTTOM: SYNC BAR + BOTÓN */}
        <div className="space-y-4 animate-in slide-in-from-bottom-8 duration-700">

          {/* Barra sync gamificada */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/15 shadow-inner">
            <p className="text-center text-sky-200 text-[10px] font-black uppercase tracking-widest mb-2">
              Tu progreso está sincronizado
            </p>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-bold text-white/40">0%</span>
              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden relative">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${userProgress?.completedSets?.length ? Math.min((userProgress.completedSets.length / 80) * 100, 100) : 3}%`,
                    background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
                    boxShadow: '0 0 8px rgba(56,189,248,0.8)',
                  }}
                />
              </div>
              <div className="flex items-center gap-1">
                {syncIcons.map((icon, i) => (
                  <div key={i}
                    className={`transition-all duration-300 ${syncStep === i ? 'scale-125 opacity-100' : 'opacity-30'}`}
                  >
                    {icon}
                  </div>
                ))}
              </div>
              <span className="text-[9px] font-bold text-white/40">100%</span>
            </div>
          </div>

          {/* Botón LET'S GO */}
          <button
            onClick={handleStartClick}
            className="relative w-full py-5 rounded-3xl font-black text-xl text-white overflow-hidden active:scale-95 transition-all border-b-4 border-blue-900"
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)',
              boxShadow: '0 0 30px rgba(37,99,235,0.6), 0 0 60px rgba(37,99,235,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
          >
            {/* Brillo superior */}
            <div className="absolute top-0 left-0 right-0 h-1/2 bg-white/10 rounded-t-3xl pointer-events-none" />
            {/* Shimmer */}
            <div
              className="absolute inset-0 pointer-events-none opacity-30"
              style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)',
                animation: 'shimmer 2.5s infinite',
              }}
            />
            <div className="relative flex items-center justify-center gap-3 drop-shadow-lg">
              <span className="tracking-widest">LET'S GO</span>
              <Play fill="currentColor" size={22} />
            </div>
          </button>
        </div>
      </div>

      {/* Shimmer keyframe */}
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}
